import { useState, useRef, useEffect } from "react";
import { useSingleton } from "../../hooks/use-singleton";
import { useApi } from "../../hooks/use-api";
import {
  apiStudentStudys,
  API_PYQ_EXAM,
  API_PYQ_EXAM_YEAR,
  API_PYQ_EXAM_EVALUATE,
} from "../../utils/api-routes";

const useHandler = () => {
  const { user, mail } = useSingleton();
  const { get, post } = useApi();

  // ── Filter State ────────────────────────────────────────────────
  const [selectedExam, setSelectedExam] = useState("");
  const [selectedYear, setSelectedYear] = useState("");

  // ── Dropdown Data ────────────────────────────────────────────────
  const [classdetails, setClassDetails] = useState({});
  const [years, setYears] = useState([]);

  // ── Quiz / UI State ──────────────────────────────────────────────
  const [openDropdown, setOpenDropdown] = useState(null);
  const [questionDetails, setQuestionDetails] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [markDetails, setMarkDetails] = useState({});
  const [showMarks, setShowMarks] = useState(false);
  const [isQuizActive, setIsQuizActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [hidePanals, setHidePanals] = useState(false);
  const [mode, setMode] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showSetupPopup, setShowSetupPopup] = useState(false);
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(false);

  // ── Refs ─────────────────────────────────────────────────────────
  const examRef = useRef(null);
  const yearRef = useRef(null);
  const selectedAnswersRef = useRef(selectedAnswers);

  // ── Enable start only when filters chosen ──────────────────
  const isStartEnabled = !!selectedExam && !!selectedYear;

  // ── 1. Load user studies details to set exam ──────────────────────
  useEffect(() => {
    const getUserStudiesDetails = async () => {
      setIsLoading(true);
      try {
        const res = await get(apiStudentStudys(mail));
        if (res) {
          setClassDetails(res?.data);
          const targetExam = res?.data?.data?.focus || res?.data?.data?.class;
          if (targetExam) {
            setSelectedExam(targetExam);
          }
        }
      } catch (e) {
        console.error("Error loading user studies", e);
      } finally {
        setIsLoading(false);
      }
    };
    if (mail) {
      getUserStudiesDetails();
    }
  }, [mail]);

  // ── 2. Load years when exam changes ──────────────────────────────
  useEffect(() => {
    if (!selectedExam) {
      setYears([]);
      setSelectedYear("");
      return;
    }
    const fetchYears = async () => {
      setIsLoading(true);
      setSelectedYear("");
      try {
        const res = await post(API_PYQ_EXAM_YEAR, { exam: selectedExam });
        if (res) setYears(res?.data?.year || []);
      } catch (e) {
        console.error("Error loading years", e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchYears();
  }, [selectedExam]);

  // ── Close dropdowns on outside click ─────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        examRef.current?.contains(e.target) ||
        yearRef.current?.contains(e.target)
      )
        return;
      setOpenDropdown(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    selectedAnswersRef.current = selectedAnswers;
  }, [selectedAnswers]);

  // ── Fetch MCQ questions ───────────────────────────────────────────
  const getMcqdetails = async () => {
    try {
      const payload = {
        mail,
        exam: selectedExam,
        year: selectedYear,
      };
      const res = await post(API_PYQ_EXAM, payload);
      if (res) {
        setQuestionDetails(res?.data);
      }
    } catch (e) {
      console.error("Error fetching MCQ", e);
      const status = e?.response?.status;

      if (status === 403) {
        setHidePanals(false);
        setIsQuizActive(false);
        setQuestionDetails([]);
      }
    }
  };

  // ── Normalize questions ───────────────────────────────────────────
  const normalizeQuestions = (apiData = []) =>
    apiData.map((q) => ({
      question: q["QUESTION"],
      options: [q["OPTION A"], q["OPTION B"], q["OPTION C"], q["OPTION D"]],
      answer: q["ANSWER"],
      questionId: q["QUESTION_ID"],
    }));

  useEffect(() => {
    if (questionDetails?.length) {
      setQuestions(normalizeQuestions(questionDetails));
    }
  }, [questionDetails]);

  // ── Quiz Duration ─────────────────────────────────────────────────
  const getQuizDuration = () => 180 * 60;

  // ── Navigation ───────────────────────────────────────────────────
  const currentQuestion = questions[currentQIndex];
  const selectedOption = selectedAnswers[currentQIndex];
  const isAnswered = selectedOption !== undefined;
  const isCorrect =
    isAnswered &&
    currentQuestion?.options[selectedOption] === currentQuestion?.answer;

  const handleOptionSelect = (index, qIndex) => {
    const targetIdx = qIndex !== undefined ? qIndex : currentQIndex;
    if (!mode && selectedAnswers[targetIdx] !== undefined) return;
    setSelectedAnswers((prev) => ({ ...prev, [targetIdx]: index }));
  };

  const handleNext = () => {
    if (currentQIndex < questions.length - 1) setCurrentQIndex((p) => p + 1);
  };
  const handlePrevious = () => {
    if (currentQIndex > 0) setCurrentQIndex((p) => p - 1);
  };

  // ── Submit ────────────────────────────────────────────────────────
  // const buildSubmitPayload = () =>
  //   questions.map((q, index) => ({
  //     QUESTION: q.question,
  //     ANSWER:
  //       selectedAnswers[index] !== undefined
  //         ? q.options[selectedAnswers[index]]
  //         : "",
  //     QUESTION_ID: q.questionId || "",
  //     YEAR: selectedYear,
  //   }));

  const buildSubmitPayload = () =>
    questions.map((q, index) => {
      const selectedIndex = selectedAnswersRef.current[index]; // ✅ latest

      return {
        QUESTION: q.question,
        ANSWER: selectedIndex !== undefined ? q.options[selectedIndex] : "",
        QUESTION_ID: q.questionId || "",
        YEAR: selectedYear,
      };
    });

  const onSubmitQuiz = async () => {
    setShowMarks(true);
    setSubmitLoading(true);
    setIsReviewMode(false);
    setShowReviewPopup(false);
    setIsQuizActive(false);
    setHidePanals(true);
    try {
      const res = await post(API_PYQ_EXAM_EVALUATE, {
        mail,
        exam: selectedExam,
        year: selectedYear,
        answers: buildSubmitPayload(),
      });
      if (res) {
        setMarkDetails(res?.data);
        setHidePanals(true);
        setPanelOpen(false);
        setSubmitLoading(false);
      }
    } catch (e) {
      setSubmitLoading(false);
      console.error("Error submitting quiz", e);
    }
  };

  const confirmStartQuiz = () => {
    setTimeRemaining(getQuizDuration());
    getMcqdetails();
    setShowSetupPopup(false);
  };

  const onStartQuiz = () => setShowSetupPopup(true);

  // ── Reset / Cancel ────────────────────────────────────────────────
  const onCancel = () => {
    setIsQuizActive(false);
    setHidePanals(true);
    setShowMarks(false);
    setMarkDetails({});
    setQuestionDetails([]);
    setQuestions([]);
    setSelectedAnswers({});
    setCurrentQIndex(0);
    setSelectedYear("");
    setHidePanals(false);
    setSubmitLoading(false);
    setTimeRemaining(0);
    setShowReviewPopup(false);
    setIsReviewMode(false);
  };

  const resetQuizState = () => {
    setIsQuizActive(true);
    setShowMarks(false);
    setMarkDetails({});
    setSelectedAnswers({});
    setCurrentQIndex(0);
    setHidePanals(true);
    setSubmitLoading(false);
    setShowReviewPopup(false);
    setIsReviewMode(false);
  };

  const onRetakeQuiz = async () => {
    resetQuizState();
    if (questionDetails?.length) {
      setQuestions(normalizeQuestions(questionDetails));
    }
  };

  const onToggleQuizBtn = () => {
    setMode((p) => !p);
    // setSelectedYear("");
  };

  const getQuestionStatus = (index) => {
    if (index === currentQIndex) return "current";

    if (selectedAnswers[index] !== undefined) {
      return "answered";
    }

    if (index < currentQIndex) {
      return "not-answered";
    }

    return "not-visited";
  };

  return {
    // filter state
    mail,
    selectedExam,
    setSelectedExam,
    selectedYear,
    setSelectedYear,
    // dropdown data
    classdetails,
    years,
    // refs
    examRef,
    yearRef,
    // ui
    openDropdown,
    setOpenDropdown,
    isStartEnabled,
    isLoading,
    panelOpen,
    setPanelOpen,
    // quiz
    questions,
    currentQuestion,
    currentQIndex,
    questionDetails,
    selectedOption,
    isAnswered,
    isCorrect,
    selectedAnswers,
    markDetails,
    showMarks,
    isQuizActive,
    user,
    mode,
    hidePanals,
    submitLoading,
    timeRemaining,
    setTimeRemaining,
    showSetupPopup,
    setShowSetupPopup,
    showReviewPopup,
    setShowReviewPopup,
    isReviewMode,
    setIsReviewMode,
    setHidePanals,
    setMode,
    setIsQuizActive,
    setCurrentQIndex,
    getQuizDuration,
    handleOptionSelect,
    handleNext,
    handlePrevious,
    onStartQuiz,
    confirmStartQuiz,
    onSubmitQuiz,
    onCancel,
    onRetakeQuiz,
    getQuestionStatus,
    onToggleQuizBtn,
  };
};

export default useHandler;
